import classNames from 'classnames/bind';
import { ContentTypeTag, SortableWidgetProps, Widget } from '@creditsights/styleguide-web';
import { ContentType } from '@creditsights/api-ui-web';
// eslint-disable-next-line import/no-unresolved
import { Layout, PlotData } from 'plotly.js-basic-dist';
import React, { forwardRef, useEffect, useState } from 'react';
import useFetchFinancials from '../../../hooks/financials/useFetchFinancials';
import useFetchFinancialsSegments from '../../../hooks/financials/useFetchFinancialsSegments';
import { SegmentGroup } from '../../../services/dtos/financials/financialsDto';
import { lineBreakLabels } from '../../../utils/stringUtils';
import DateFooter from '../common/DateFooter';
import ChartPlotly from '../RiskProducts/ChartPlotly';
import {
  plotDefaultConfig,
  plotDefaultData,
  plotLayoutConfig,
  plotLayoutConfigSmall,
} from './FinancialsSegments.presentational.constants';

import styles from './FinancialsSegments.module.scss';

const cx = classNames.bind(styles);

export interface FinancialsSegmentWidgetProps extends SortableWidgetProps {
  companyTagId: string;
  segmentGroupIndex: number | null;
  noPrint?: boolean;
}

const FinancialsSegmentsWidget = (
  { companyTagId, segmentGroupIndex, noPrint, ...sortableWidgetProps }: FinancialsSegmentWidgetProps,
  ref
): React.ReactElement | null => {
  const [plotData, setPlotData] = useState<Partial<PlotData>[]>(plotDefaultData);
  const [segmentGroup, setSegmentGroup] = useState<SegmentGroup | null>(null);
  const [layoutConfig, setLayoutConfig] = useState<Partial<Layout>>(plotLayoutConfig);

  const {
    data: financialsDto,
    isLoading: isSegmentsLoading,
    isError: isSegmentsError,
    refetch: refetchFinancials,
  } = useFetchFinancials(companyTagId);
  const segmentGroups = useFetchFinancialsSegments(financialsDto);

  useEffect(() => {
    if (segmentGroups && segmentGroups.length > 0) {
      setSegmentGroup(segmentGroupIndex ? segmentGroups[segmentGroupIndex] : segmentGroups[0]);
    }
  }, [segmentGroups]);

  useEffect(() => {
    setPlotData([
      {
        ...plotDefaultData[0],
        values: segmentGroup?.values,
        labels: lineBreakLabels(segmentGroup?.labels || [], 40),
      },
    ]);
    setLayoutConfig((segmentGroup?.labels.length || 0) < 4 ? plotLayoutConfigSmall : plotLayoutConfig);
  }, [segmentGroup]);

  return (
    segmentGroup && (
      <div className={cx('body')}>
        <Widget
          classNames={cx({ 'no-print': noPrint })}
          key={segmentGroup.title}
          headerTitle={segmentGroup.title}
          dataTestId="financials-widget"
          isLoading={isSegmentsLoading}
          isError={isSegmentsError}
          onRetryAfterErrorClick={refetchFinancials}
          ref={ref}
          footer={segmentGroup.publishDate ? <DateFooter date={segmentGroup.publishDate} /> : null}
          {...sortableWidgetProps}
          fixedHeight={false}
        >
          <>
            <div className={cx('content-type-tag')}>
              <ContentTypeTag contentType={ContentType.Financials} dataTestId="financials-widget-content-type-tag" />
            </div>
            <ChartPlotly data={plotData} layout={layoutConfig} config={plotDefaultConfig} />
          </>
        </Widget>
      </div>
    )
  );
};

export default forwardRef(FinancialsSegmentsWidget);
